"use strict";

import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { languages } from "../../Data/languages.js";
import {
  attachSubtitles,
  isSrtFile,
  uploadSubtitleToStorage,
} from "./uploadSubtitle.js";

const sortedLanguages = [...languages].sort((a, b) =>
  a.name.localeCompare(b.name)
);

/**
 * Uploads SRT files to an existing project, mirroring the subtitle options
 * from the project creation details screen.
 */
const ProjectSubtitleUpload = ({
  projectId,
  originalSubtitleObjectName,
  onSubtitlesAttached,
}) => {
  const [subtitleFiles, setSubtitleFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const addSubtitleFiles = (files) => {
    const selectedFiles = Array.from(files || []);
    const invalidFile = selectedFiles.find((file) => !isSrtFile(file));
    if (invalidFile) {
      toast.error("Subtitle files must use the .srt extension");
      return;
    }

    setSubtitleFiles((current) => {
      let hasOriginal =
        Boolean(originalSubtitleObjectName) ||
        current.some((subtitle) => subtitle.role === "original");
      const additions = selectedFiles.map((file) => {
        const role = hasOriginal ? "translated" : "original";
        hasOriginal = true;
        return {
          id: uuidv4(),
          file,
          fileName: file.name,
          role,
          languageCode: "",
          progress: 0,
          objectName: "",
        };
      });
      return [...current, ...additions];
    });
  };

  const updateSubtitleFile = (id, updates) => {
    setSubtitleFiles((current) =>
      current.map((subtitle) =>
        subtitle.id === id ? { ...subtitle, ...updates } : subtitle
      )
    );
  };

  const removeSubtitleFile = (id) => {
    setSubtitleFiles((current) =>
      current.filter((subtitle) => subtitle.id !== id)
    );
  };

  const uploadSubtitleFile = (subtitle) => {
    if (subtitle.objectName) {
      return Promise.resolve(subtitle);
    }

    return uploadSubtitleToStorage({
      projectId,
      id: subtitle.id,
      file: subtitle.file,
      onProgress: (progress) => updateSubtitleFile(subtitle.id, { progress }),
    }).then((objectName) => {
      const uploaded = { ...subtitle, objectName, progress: 100 };
      updateSubtitleFile(subtitle.id, uploaded);
      return uploaded;
    });
  };

  const validateSubtitleFiles = () => {
    const originalFiles = subtitleFiles.filter(
      (subtitle) => subtitle.role === "original"
    );
    if (originalFiles.length > 1) {
      throw new Error("Only one original subtitle file is allowed");
    }

    const translatedLanguages = new Set();
    for (const subtitle of subtitleFiles) {
      if (subtitle.role !== "translated") {
        continue;
      }
      if (!subtitle.languageCode) {
        throw new Error(`Select a language for ${subtitle.fileName}`);
      }
      if (translatedLanguages.has(subtitle.languageCode)) {
        throw new Error(
          `Only one translated subtitle is allowed for ${subtitle.languageCode}`
        );
      }
      translatedLanguages.add(subtitle.languageCode);
    }
  };

  const handleUpload = async () => {
    try {
      validateSubtitleFiles();
    } catch (error) {
      toast.error(error.message);
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await Promise.all(subtitleFiles.map(uploadSubtitleFile));
      const copiedSubtitles = await attachSubtitles(projectId, uploaded);

      onSubtitlesAttached?.(copiedSubtitles);
      setSubtitleFiles([]);
      toast.success("Subtitle files uploaded");
    } catch (error) {
      console.error("Error uploading subtitle files:", error);
      toast.error(
        error.response?.data?.message || error.message || "Error uploading subtitle files"
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Upload subtitle files
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Add one original SRT for forced alignment and optional translated SRTs
          by language for translation mapping. Uploading a new file replaces the
          existing one for the same role and language.
        </Typography>
      </Box>

      <Button
        component="label"
        variant="outlined"
        startIcon={<UploadFileIcon />}
        disabled={isUploading}
        sx={{ alignSelf: "flex-start" }}
      >
        Choose SRT files
        <input
          hidden
          multiple
          type="file"
          accept=".srt,application/x-subrip,text/plain"
          onChange={(event) => {
            addSubtitleFiles(event.target.files);
            event.target.value = "";
          }}
        />
      </Button>

      {subtitleFiles.map((subtitle) => (
        <Paper key={subtitle.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography fontWeight={600} noWrap>
                {subtitle.fileName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              <FormControl fullWidth size="small">
                <Select
                  value={subtitle.role}
                  disabled={isUploading}
                  onChange={(event) =>
                    updateSubtitleFile(subtitle.id, {
                      role: event.target.value,
                      languageCode:
                        event.target.value === "original"
                          ? ""
                          : subtitle.languageCode,
                    })
                  }
                >
                  <MenuItem value="original">Original</MenuItem>
                  <MenuItem value="translated">Translated</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5} md={4}>
              {subtitle.role === "translated" && (
                <FormControl fullWidth size="small">
                  <Select
                    value={subtitle.languageCode}
                    displayEmpty
                    disabled={isUploading}
                    onChange={(event) =>
                      updateSubtitleFile(subtitle.id, {
                        languageCode: event.target.value,
                      })
                    }
                  >
                    <MenuItem value="" disabled>
                      Select language
                    </MenuItem>
                    {sortedLanguages.map((language) => (
                      <MenuItem key={language.code} value={language.code}>
                        {language.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={2} md={1} textAlign="right">
              <IconButton
                aria-label={`Remove ${subtitle.fileName}`}
                disabled={isUploading}
                onClick={() => removeSubtitleFile(subtitle.id)}
                sx={{ color: "#ef4444" }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Grid>
            {subtitle.progress > 0 && subtitle.progress < 100 && (
              <Grid item xs={12}>
                <LinearProgress
                  variant="determinate"
                  value={subtitle.progress}
                  sx={{ height: 8, borderRadius: 99 }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      ))}

      {subtitleFiles.length > 0 && (
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={isUploading}
          sx={{ alignSelf: "flex-start" }}
        >
          {isUploading ? "Uploading..." : "Upload SRT files"}
        </Button>
      )}
    </Stack>
  );
};

export default ProjectSubtitleUpload;
